import PropTypes from "prop-types";
import {useMemo, useState} from "react";
import {v4 as uuidv4} from "uuid";
import {
    Table as MuiTable,
    TableBody,
    TableContainer,
    TableRow,
    IconButton,
    Icon,
} from "@mui/material";

import SoftBox from "../SoftBox";
import SoftButton from "../SoftButton";
import SoftTypography from "../SoftTypography";
import SoftAvatar from "../Avatar";

import colors from "../../assets/theme/base/colors";
import typography from "../../assets/theme/base/typography";
import borders from "../../assets/theme/base/borders";

/**
 * Custom pagination row. Built from primitives instead of using MUI's
 * <TablePagination> so the global Soft UI input theme (which adds
 * rounded backgrounds, borders, and width:100% to .MuiInputBase-root)
 * can't fight our layout.
 */
function CustomPagination({
                              count,
                              page,
                              rowsPerPage,
                              rowsPerPageOptions,
                              onPageChange,
                              onRowsPerPageChange,
                          }) {
    const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
    const from = count === 0 ? 0 : page * rowsPerPage + 1;
    const to = Math.min(count, (page + 1) * rowsPerPage);

    return (
        <SoftBox
            sx={({palette, borders: b}) => ({
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
                px: 2,
                py: 1.25,
                borderTop: `1px solid ${palette.grey[200]}`,
                "& .pg-label, & .pg-count": {
                    fontSize: "0.8125rem",
                    color: palette.text.main,
                    whiteSpace: "nowrap",
                },
                "& .pg-select": {
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                    width: "auto",
                    minWidth: 0,
                    padding: "4px 26px 4px 10px",
                    fontSize: "0.8125rem",
                    fontFamily: "inherit",
                    color: palette.text.main,
                    backgroundColor: palette.white.main,
                    border: `1px solid ${palette.grey[300]}`,
                    borderRadius: b.borderRadius.md,
                    backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23475569' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 8px center",
                    cursor: "pointer",
                    outline: "none",
                    "&:focus": {borderColor: palette.primary.main},
                },
            })}
        >
            <span className="pg-label">Rows per page</span>
            <select
                className="pg-select"
                value={rowsPerPage}
                onChange={onRowsPerPageChange}
            >
                {rowsPerPageOptions.map((n) => (
                    <option key={n} value={n}>{n}</option>
                ))}
            </select>
            <span className="pg-count">
                {from}–{to} of {count}
            </span>
            <SoftBox sx={{display: "flex", gap: 0.5}}>
                <IconButton
                    size="small"
                    onClick={(e) => onPageChange(e, page - 1)}
                    disabled={page === 0}
                    aria-label="previous page"
                >
                    <Icon fontSize="small">chevron_left</Icon>
                </IconButton>
                <IconButton
                    size="small"
                    onClick={(e) => onPageChange(e, page + 1)}
                    disabled={page >= lastPage}
                    aria-label="next page"
                >
                    <Icon fontSize="small">chevron_right</Icon>
                </IconButton>
            </SoftBox>
        </SoftBox>
    );
}

CustomPagination.propTypes = {
    count: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
};

/**
 * Generic, paginated table.
 *
 * columns: [
 *   {
 *     name: "First Name",          // header label (rendered uppercase)
 *     value: "firstName",          // row key
 *     align: "left" | "center" | "right",
 *     width: "auto" | "10",        // optional
 *     renderCell: (row) => JSX,    // optional; defaults to <text>{row[value]}</text>
 *   },
 *   ...
 * ]
 *
 * Special cell helpers (renderArrayCell, renderImageCell, etc.) are exported
 * below for ergonomic use from call sites.
 */
function DataTable({
                       columns,
                       rows,
                       rowsPerPageOptions,
                       defaultRowsPerPage,
                   }) {
    const {light} = colors;
    const {size, fontWeightBold} = typography;
    const {borderWidth} = borders;

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return rows.slice(start, start + rowsPerPage);
    }, [rows, page, rowsPerPage]);

    const renderColumns = columns.map(({name, align, width}, key) => {
        const pl = key === 0 ? 3 : 1;
        const pr = key === columns.length - 1 ? 3 : 1;

        return (
            <SoftBox
                key={name}
                component="th"
                width={width || "auto"}
                pt={1.5}
                pb={1.25}
                pl={align === "left" ? pl : 3}
                pr={align === "right" ? pr : 3}
                textAlign={align}
                fontSize={size.md}
                fontWeight={fontWeightBold}
                backgroundColor="#e8b53b"
                color="#3b5998"
                borderBottom={`${borderWidth[1]} solid #d0a22e`}
            >
                {name?.toUpperCase()}
            </SoftBox>
        );
    });

    const renderRows = paginatedRows.map((row, rowIdx) => {
        const tableRow = columns.map(({value, align, renderCell}, key) => {
            const cellContent = renderCell
                ? renderCell(row)
                : (
                    <SoftTypography
                        variant="button"
                        fontWeight="regular"
                        color="text"
                        sx={{display: "inline-block", width: "max-content"}}
                    >
                        {row[value]}
                    </SoftTypography>
                );

            const isFirst = key === 0;
            const isLast = key === columns.length - 1;
            const pl = isFirst ? 3 : 1;
            const pr = isLast ? 3 : 1;

            return (
                <SoftBox
                    key={uuidv4()}
                    component="td"
                    pt={1.25}
                    pb={1.25}
                    pl={align === "left" ? pl : 3}
                    pr={align === "right" ? pr : 3}
                    textAlign={align}
                    borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null}
                >
                    {cellContent}
                </SoftBox>
            );
        });

        return <TableRow key={`row-${rowIdx}`}>{tableRow}</TableRow>;
    });

    return (
        <SoftBox
            sx={({palette, borders}) => ({
                backgroundColor: palette.white.main,
                border: `1px solid ${palette.grey[200]}`,
                borderRadius: borders.borderRadius.lg,
                // No overflow:hidden here. With it, the toolbar's outer
                // edges (the "Rows per page" label or the action arrows
                // depending on alignment) got clipped instead of laying
                // out cleanly. Without it, the MUI table corners stay
                // square at the bottom but the visible 1px border still
                // looks like a clean rounded card.
            })}
        >
            <TableContainer>
                <MuiTable>
                    <SoftBox component="thead">
                        <TableRow>{renderColumns}</TableRow>
                    </SoftBox>
                    <TableBody>{renderRows}</TableBody>
                </MuiTable>
            </TableContainer>
            <CustomPagination
                count={rows.length}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={rowsPerPageOptions}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </SoftBox>
    );
}

DataTable.defaultProps = {
    columns: [],
    rows: [],
    rowsPerPageOptions: [5, 10, 20],
    defaultRowsPerPage: 5,
};

DataTable.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.string,
        align: PropTypes.oneOf(["left", "center", "right"]),
        width: PropTypes.string,
        renderCell: PropTypes.func,
    })),
    rows: PropTypes.arrayOf(PropTypes.object),
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    defaultRowsPerPage: PropTypes.number,
};

/**
 * Renders an avatar+name pair, where row[key] is [imageSrc, name].
 * Use as `renderCell: (row) => renderArrayCell(row, 'name')`.
 */
export function renderArrayCell(row, key) {
    const value = row[key];
    if (!Array.isArray(value)) return null;
    return (
        <SoftBox display="flex" alignItems="center" py={0.5} px={1}>
            <SoftBox mr={2}>
                <SoftAvatar src={value[0]} name={value[1]} variant="rounded" size="sm"/>
            </SoftBox>
            <SoftTypography variant="button" fontWeight="medium" sx={{width: "max-content"}}>
                {value[1]}
            </SoftTypography>
        </SoftBox>
    );
}

/**
 * Renders a base64 profile picture as a small avatar.
 */
export function renderBase64ImageCell(row, key) {
    const value = row[key];
    if (!value) return null;
    return (
        <SoftBox display="flex" justifyContent="center">
            <SoftAvatar
                src={`data:image/png;base64,${value}`}
                alt={key}
                variant="rounded"
                size="sm"
                shadow="sm"
            />
        </SoftBox>
    );
}

/**
 * Renders a colored gradient pill (used for status / role columns).
 * `color` is a SoftButton color name ("success", "error", "primary", etc.).
 *
 * Width is fixed (not min-width) so every pill in every column reads as
 * the same size, no matter the label length. 130px comfortably fits the
 * longest current label ("SYSTEM ADMIN" / "COMPANY ADMIN") in caps.
 */
const PILL_WIDTH = 130;

export function renderPill(label, color = "primary") {
    return (
        <SoftBox display="flex" justifyContent="center">
            <SoftButton
                variant="gradient"
                color={color}
                size="small"
                sx={{
                    width: PILL_WIDTH,
                    minWidth: PILL_WIDTH,
                    px: 1,
                    py: 0.5,
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                }}
            >
                {label}
            </SoftButton>
        </SoftBox>
    );
}

export default DataTable;
